import { groupBy, sortBy } from "lodash";

import { derived, Writable } from "svelte/store";
import type { DiscountResult, SfPriceGroupResult } from "@retailer/services/discounts_service";
import DiscountsLookupStore from "@retailer/svelte/stores/DiscountsLookupStore";
import { DISCOUNT_AMOUNT_TYPES } from "@shared/types/Deals";

interface MixDeal {
  dealId: number;
  dealName: string;
  dealLabel: string;
  discounts: DiscountResult[];
  inventoryId: number;
}

interface QuantityWithRequirementsDiscountGroup {
  discounts: DiscountResult[];
  inventoryId: number;
  pgId: string;
  sfPriceGroup: SfPriceGroupResult;
}

interface MixQuantityDiscountGroup {
  discounts: DiscountResult[];
  inventoryId: number;
  dealId: number;
}

interface InventoryDiscounts {
  mixDeals: MixDeal[];
  quantityDiscounts: DiscountResult[];
  quantityWithRequirementsDiscountGroups: QuantityWithRequirementsDiscountGroup[];
  ripMixDeals: MixDeal[];
  ripQuantityDiscountGroups: MixQuantityDiscountGroup[];
}

const buildInventoryDiscounts = (
  discountsByInventoryId: Record<number, DiscountResult[]>,
  inventoryId: number,
): InventoryDiscounts => {
  const discountsForInventory = discountsByInventoryId[inventoryId];

  if (!discountsForInventory) {
    return buildEmptyInventoryDiscounts();
  }

  const mixDiscounts = [];
  const quantityDiscounts = [];
  const quantityWithRequirementsDiscounts = [];
  const ripMixDiscounts = [];
  const ripQuantityDiscounts = [];

  const sortedDiscountsForInventory = sortBy(discountsForInventory, "case_count");

  sortedDiscountsForInventory.forEach(discount => {
    if (discount.amount_type === DISCOUNT_AMOUNT_TYPES.RIP) {
      if (discount.is_mix_match) {
        ripMixDiscounts.push(discount);
      } else {
        ripQuantityDiscounts.push(discount);
      }
    } else if (
      discount.amount_type === DISCOUNT_AMOUNT_TYPES.QUANTITY_WITH_REQUIREMENTS &&
      (discount.sf_price_group_sf_pk || discount.product_group_id || discount.discount_type)
    ) {
      quantityWithRequirementsDiscounts.push(discount);
    } else if (discount.is_mix_match) {
      mixDiscounts.push(discount);
    } else {
      quantityDiscounts.push(discount);
    }
  });

  const prepareMixDeals = (discountsToGroup: DiscountResult[]): MixDeal[] => {
    const discountsByDealId = groupBy(
      discountsToGroup,
      discount => discount.deal_id || discount.product_group_id,
    );

    return Object.values(discountsByDealId).map(discounts => {
      return {
        dealId: discounts[0].deal_id,
        dealName: discounts[0].deal_tier_name || "Mix and Match",
        dealLabel: discounts[0].deal_tier_label || "",
        discounts,
        inventoryId,
      };
    });
  };

  const quantityWithRequirementsDiscountsBySfPk = groupBy(
    quantityWithRequirementsDiscounts,
    discount =>
      discount.sf_price_group_sf_pk || discount.product_group_id || discount.discount_type,
  );

  const groupedQuantityWithRequirementsDiscounts = Object.entries(
    quantityWithRequirementsDiscountsBySfPk,
  ).map(([pgId, discounts]) => {
    return {
      discounts,
      inventoryId,
      pgId: pgId,
      sfPriceGroup: discounts[0].sf_price_group,
    };
  });

  const ripQuantityDiscountsByDealId = groupBy(ripQuantityDiscounts, "deal_id");

  const groupedRipQuantityDiscounts = Object.entries(ripQuantityDiscountsByDealId).map(
    ([dealId, discounts]) => {
      return {
        dealId: Number(dealId),
        discounts,
        inventoryId,
      };
    },
  );

  return {
    mixDeals: prepareMixDeals(mixDiscounts),
    quantityDiscounts,
    quantityWithRequirementsDiscountGroups: groupedQuantityWithRequirementsDiscounts,
    ripMixDeals: prepareMixDeals(ripMixDiscounts),
    ripQuantityDiscountGroups: groupedRipQuantityDiscounts,
  };
};

const buildEmptyInventoryDiscounts = (): InventoryDiscounts => ({
  mixDeals: [],
  quantityDiscounts: [],
  quantityWithRequirementsDiscountGroups: [],
  ripMixDeals: [],
  ripQuantityDiscountGroups: [],
});

export interface InventoryDiscountsStoreState {
  discounts: DiscountResult[];
  getDiscountsForInventoryId(inventoryId: number): InventoryDiscounts;
}

export const InventoryDiscountsStore = derived<
  Writable<DiscountResult[]>,
  InventoryDiscountsStoreState
>(DiscountsLookupStore, discounts => {
  const discountsByInventoryId = groupBy(discounts, "inventory_id");

  const inventoryDiscountsByInventoryId = {};

  Object.keys(discountsByInventoryId).forEach(inventoryId => {
    inventoryDiscountsByInventoryId[inventoryId] = buildInventoryDiscounts(
      discountsByInventoryId,
      Number(inventoryId),
    );
  });

  const getDiscountsForInventoryId = (inventoryId: number): InventoryDiscounts =>
    inventoryDiscountsByInventoryId[inventoryId] || buildEmptyInventoryDiscounts();

  return {
    discounts,
    getDiscountsForInventoryId,
  };
});
